import type { ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import styles from './PageTransition.module.css';

export interface PageTransitionProps {
  children: ReactNode;
  /**
   * Value that re-triggers the enter animation when it changes. Defaults to the route pathname, so
   * the animation replays on navigation (but NOT on same-page search-param changes, e.g. tabs).
   */
  transitionKey?: string;
}

/**
 * Wraps page content in a calm, premium enter animation (soft fade + slight upward motion) that
 * replays on each route change. Animation is `opacity`/`transform`-only (no layout shift) and is
 * disabled under `prefers-reduced-motion`. Wrap a router `<Outlet />` (or a page) with it.
 */
export function PageTransition({ children, transitionKey }: PageTransitionProps) {
  // `resolvedLocation` (not `location`) — `location` flips to the TARGET path the instant a
  // navigation is merely requested, while the outgoing route's component is often still the one
  // actually rendered (e.g. an async `onSubmit` still awaiting `navigate()`). Keying off it would
  // remount that still-mounted component mid-navigation and wipe its state (confirmed: it was
  // clearing the login form's typed email/password right before a successful redirect).
  // `resolvedLocation` only updates once the new route has actually taken over.
  const pathname = useRouterState({
    select: (state) => (state.resolvedLocation ?? state.location).pathname,
  });
  // Remounting the keyed element is what restarts the CSS animation on navigation.
  return (
    <div key={transitionKey ?? pathname} className={styles.page}>
      {children}
    </div>
  );
}
